/**
 * Pure state-change → stimulus translator for the flow connector.
 *
 * Given the previous and current {@link FlowExecution} snapshots, decides
 * whether the runner should be woken with a fresh turn and (if so) what
 * kind of stimulus to render. Returns `null` when no turn is warranted
 * (e.g. the snapshots are deep-equal).
 *
 * Today's FlowOrchestrator subscribes to `adapter.onStateChange(...)` and
 * forwards the snapshot to the host without kicking a turn — the host
 * issues explicit turn-kicking commands (`approve_flow_node`, etc.). Phase
 * 3 of the flow-connector extraction adds this function as the connector-
 * side decision point so a future direct path (FlowAdapter →
 * SessionStimulusBus → runner) can reproduce today's coalesced "state
 * changed → maybe a turn" semantics without going through the orchestrator.
 *
 * Default behaviour mirrors today exactly: any structural change in the
 * FlowExecution snapshot produces a single `{ kind: "state_changed" }`
 * stimulus. Phase 4 may refine this with kind discriminators (approval-
 * received, input-received, etc.) once the orchestrator's onStateChange
 * subscription is removed; until then we keep gating purely on equality.
 *
 * @docLink packages/factory-assets/flow#stimulus-driver
 */

import type { FlowExecution } from "@skaile/workspaces/types";
import type { TurnStimulus } from "./prompt-fragments.js";

/**
 * Decide whether a state transition warrants kicking an agent turn, and
 * which kind of stimulus to render. Returns `null` to suppress turn-kicking
 * when nothing observable changed.
 *
 * Pure — depends only on its inputs.
 *
 * @param oldExec - Previous FlowExecution snapshot (or `undefined` on the
 *   first emission, e.g. immediately after `connect()`).
 * @param newExec - Current FlowExecution snapshot.
 * @returns A {@link TurnStimulus} when a turn should be kicked, or `null`.
 *
 * @docLink packages/factory-assets/flow#compute-stimulus
 */
export function computeStimulus(
  oldExec: FlowExecution | undefined,
  newExec: FlowExecution,
): TurnStimulus | null {
  // First emission (no prior snapshot) — treat as a full state change so
  // downstream consumers can latch onto the initial state.
  if (!oldExec) {
    return { kind: "state_changed" };
  }

  // Identity short-circuit (cheap; mutate() returns a fresh object on every
  // change so reference inequality is the common case).
  if (oldExec === newExec) {
    return null;
  }

  // Structural compare — JSON.stringify is good enough for FlowExecution
  // because it has stable key ordering produced by the engine and contains
  // no functions or class instances. Avoids pulling in a deep-equal dep.
  if (snapshotsEqual(oldExec, newExec)) {
    return null;
  }

  return { kind: "state_changed" };
}

/**
 * Cheap structural equality for two FlowExecution snapshots. Mirrors the
 * comparison the orchestrator's onStateChange subscription would do if it
 * cared (it currently re-fires unconditionally).
 *
 * @internal
 */
function snapshotsEqual(a: FlowExecution, b: FlowExecution): boolean {
  if (a === b) return true;
  // Hot-path guard: differing top-level fields short-circuit before the
  // expensive serialize.
  if (a.runId !== b.runId) return false;
  if (a.flowId !== b.flowId) return false;
  if (a.status !== b.status) return false;
  if (a.done !== b.done) return false;
  if (a.autonomousMode !== b.autonomousMode) return false;

  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    // Defensive: if a snapshot somehow contains a circular ref the safe
    // answer is "treat as different" so we don't suppress a real change.
    return false;
  }
}
